import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faSitemap,
  faLayerGroup,
  faIdCard,
  faShieldHalved,
  faCompass,
} from "@fortawesome/free-solid-svg-icons";

import PSProfiles from "./PSProfiles";
// import PSOpportunities from "./PSOpportunities"; // commented out — ToDo Later
import PSCircles from "./PSCircles";
import PSIncome from "./PSIncome";
import PSChapter from "./PSChapter";
import PSAdminApprovals from "./PSAdminApprovals";
import PSVision from "./PSVision";

/**
 * ── Role model (as of this pass) ────────────────────────────────────────
 * Super Admin  — hardcoded userCode allow-list. ONLY job: approve a new
 *                Chapter/Circle to go live (PSAdminApprovals).
 * Chapter Director — approve/add/deactivate members of their own chapter.
 * Circle Director   — approve/add/deactivate chapters affiliated with their
 *                      own circle.
 * There is no separate generic "Admin" role right now — your previous file
 * had "Admin" and "Super Admin" as two tabs pointed at the same screen and
 * gated by the same flag, which was a leftover duplicate, not two features.
 * Collapsed to one tab, gated only by isSuperAdmin(userCode).
 * ─────────────────────────────────────────────────────────────────────────
 */

// Add more approver userCodes here as needed — this is the only place that
// needs to change to grant/revoke Super Admin.
const SUPER_ADMIN_CODES = ["TU243BM5850150"];

function isSuperAdmin(userCode) {
  return Boolean(userCode) && SUPER_ADMIN_CODES.includes(userCode);
}

export default function PSHome() {
  // const authState = useSelector((state) => state.authState || {});
  const authState = useSelector(state => state.authReducer);
  const userName = authState?.user?.name || "User";
  const userCode = authState?.user?.think5whyscode || "";
  const superAdmin = isSuperAdmin(userCode);
  // Route is now /pehchaansetu/:mode?/:value?
  //   /pehchaansetu/foundher              -> theme only
  //   /pehchaansetu/circle/:circleSlug    -> opens Circles tab, deep-linked
  //   /pehchaansetu/chapter/:chapterSlug  -> opens Chapters tab, deep-linked
  const { mode, value } = useParams();
  const isFoundHer = mode === "foundher";
  const deepLinkCircleSlug = mode === "circle" ? value : null;
  const deepLinkChapterSlug = mode === "chapter" ? value : null;

  const [tab, setTab] = useState(
    deepLinkChapterSlug ? "chapters" : deepLinkCircleSlug ? "circles" : "profiles"
  );

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: faChartLine },
    { id: "chapters", label: "Chapters", icon: faSitemap },
    { id: "circles", label: "Circles", icon: faLayerGroup },
    // { id: "opportunities", label: "Jobs", icon: faBriefcase }, // ToDo Later
    { id: "profiles", label: "Profiles", icon: faIdCard },
    { id: "vision", label: "Vision", icon: faCompass },
    ...(superAdmin ? [{ id: "superAdmin", label: "Super Admin", icon: faShieldHalved }] : []),
  ];

  return (
    <div className="flex min-h-screen flex-col bg-[#0F0B1A]">
      {/* <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto text-sm">
        {JSON.stringify(authState, null, 2)}
      </pre> */}
      <header
        className={`relative overflow-hidden px-4 pb-5 pt-5 text-center ${
          isFoundHer
            ? "bg-gradient-to-br from-rose-600/30 via-fuchsia-600/20 to-[#0F0B1A]"
            : "bg-gradient-to-br from-violet-600/25 via-fuchsia-600/15 to-[#0F0B1A]"
        }`}
      >
        <div
          className="text-lg font-extrabold text-white lg:text-2xl"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          <span aria-hidden>🌉</span> {isFoundHer ? "Her " : ""}PehchaanSetu
        </div>
        <div className="mt-1 text-[11px] text-white/60 lg:text-sm">
          Build for Local: Pehchaan That Moves You Forward —{" "}
          <span className="font-semibold text-white/80">{userName}</span>{" "}
          {userCode && <span className="text-white/40">· {userCode}</span>}
        </div>
      </header>

      {/* Tab bar — icon + label pills, horizontally scrollable on mobile */}
      <nav className="sticky top-0 z-10 border-b border-white/10 bg-[#0F0B1A]/90 px-2 py-2 backdrop-blur">
        <div className="flex gap-1.5 overflow-x-auto scrollbar-none">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={[
                "flex shrink-0 items-center gap-1.5 rounded-full w-fit px-3 py-1.5 text-[11px] font-semibold transition lg:text-sm",
                tab === t.id
                  ? "bg-gradient-to-r from-violet-500 via-fuchsia-500 to-rose-500 text-white shadow-md shadow-fuchsia-500/20"
                  : "text-white/50 hover:bg-white/[0.06] hover:text-white/80",
              ].join(" ")}
            >
              <FontAwesomeIcon icon={t.icon} className="text-[11px]" />
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="flex-1">
        {tab === "profiles" && <PSProfiles />}
        {/* {tab === "opportunities" && <PSOpportunities />} */}
        {tab === "chapters" && <PSChapter deepLinkSlug={deepLinkChapterSlug} />}
        {tab === "circles" && <PSCircles deepLinkSlug={deepLinkCircleSlug} />}
        {tab === "dashboard" && <PSIncome />}
        {tab === "vision" && <PSVision />}
        {tab === "superAdmin" && superAdmin && <PSAdminApprovals />}
      </div>

      <footer className="mt-4 border-t border-white/10 py-5 text-white/40">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 text-[11px] md:flex-row lg:text-xs">
          <div>© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/about-us" className="hover:text-fuchsia-300">
              About
            </Link>
            <Link to="/privacy-policy" className="hover:text-fuchsia-300">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-fuchsia-300">
              Terms of Use
            </Link>
            <Link to="/contact-us" className="hover:text-fuchsia-300">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
